import queryString, { ParsedQuery } from 'query-string';
import { matchPath } from 'react-router';

import { getViewportElement, noop } from '@philips-emr/tws-utils';

import { getPathParams, resolvePath } from '../util';

import { getContextName } from './configuration-service';

const _inited = Symbol('inited');

const _history = Symbol('history');
const _location = Symbol('location');

const _params = Symbol('params');
const _match = Symbol('match');
const _matchListeners = Symbol('_matchListeners');

interface ListenerParams {
  fromPush: boolean;
  pathname: string;
}

interface Params {
  [key: string]: string | number;
}

export interface Match {
  isExact: boolean;
  params: Params;
  path: string;
  url: string;
}

export interface Options {
  scrollToTop: boolean;
}

export type PathChangeListener = (path: string) => Promise<any>;

class HistoryService {

  [_inited]: any = null;

  [_history]: any = null;
  [_location]: any = null;

  [_params]: Params;
  [_match]: Match;
  [_matchListeners]: Function[] = [];

  private popped: boolean = false;

  private pathChangeListeners: Set<PathChangeListener> = new Set();

  private scrollToTop(): void {
    const viewport = getViewportElement(true);

    viewport.scrollTo({ top: 0, behavior: 'auto' });
  }

  private getCallbacks(path: string): Promise<any>[] {
    return Array.from(this.pathChangeListeners).map((listener) => listener(path));
  }

  private historyListener = ({ fromPush, pathname }: ListenerParams): void => {
    if (fromPush || this.popped) {
      this.popped = false;
      return;
    }
      
    this.getCallbacks(pathname);
  };

  addListener(listener: PathChangeListener): Function {
    if (this.pathChangeListeners.has(listener)) {
      throw Error('[History Service]: Listener already added.');
    }

    let unlisten: Function = noop;
    if (this.pathChangeListeners.size === 0) {
      unlisten = listenHistoryChange(this.historyListener);
    }

    this.pathChangeListeners.add(listener);

    return () => {
      this.pathChangeListeners.delete(listener);

      if (this.pathChangeListeners.size === 0) {
        unlisten();
      }
    };
  }

  push(path: string, params: Params = {}, options: Options = { scrollToTop: true }): void {
    this.checkInited();

    const resolvedPath = resolvePath(path, { ...this[_params], ...params });

    const promises = this.getCallbacks(resolvedPath);
    Promise.all(promises).then(() => {
      this[_history].push({
        fromPush: true,
        pathname: resolvedPath,
        state: {
          from: this[_location].pathname,
        },
      });

      if (options.scrollToTop) {
        this.scrollToTop();
      }
    });
  }

  back(options: Options = { scrollToTop: true }): void {
    this.checkInited();

    const previousPath = this[_location].state?.from;
    const promises = this.getCallbacks(previousPath);

    Promise.all(promises).then(() => {
      this.popped = true;
      this[_history].goBack();

      if (options.scrollToTop) {
        this.scrollToTop();
      }
    });
  }

  getCurrentRoute(): string {
    return window.location.pathname;
  }

  getCurrentRouteParams(): Params {
    return this[_params];
  }

  getCurrentRouteQueryParams(): ParsedQuery {
    return queryString.parse(window.location.search);
  }

  getMatch(): Match {
    return this[_match];
  }

  checkInited(): void {
    if (!instance[_inited]) {
      throw new Error('[HistoryService]: it is not initialized!');
    }
  }

  makeURL(url: string): string {
    let prettyURL = `/${url}`.replace(new RegExp('//+', 'g'), '/');

    const context = getContextName();
    if (context && prettyURL.startsWith(context)) {
      prettyURL = prettyURL.replace(context, '');
    }
    return prettyURL;
  }

}

const instance = new HistoryService();

interface HistoryServiceArgs {
  history: any;
  location: any;
  context?: string;
}

function init({ history, location }: HistoryServiceArgs): void {
  instance[_history] = history;
  instance[_location] = location;
  instance[_inited] = true;
}

function listenHistoryChange(callback: Function): Function {
  if (!instance[_history]) {
    return noop;
  }
  return instance[_history].listen(callback);
}

function listenMatch(callback: Function): Function {
  const set = instance[_matchListeners];
  set.push(callback);
  return () => {
    const i  = set.indexOf(callback);
    set.splice(i, 1);
  };
}

function setMatch(match: Match): void {
  const { params } = match;
  instance[_params] = params;
  instance[_match] = match;
  instance[_matchListeners].forEach(fn => fn(match));
}

function matchRouteExactWithQueryParam(routeMatch: string, route: string = instance.getCurrentRoute()): boolean {
  const context = getContextName();
  const url = `/${context}/${routeMatch}`.replace(new RegExp('//+', 'g'), '/');

  let routeMatchNormalized = url.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  routeMatchNormalized = `^${routeMatchNormalized}$`;

  const routeParams = getPathParams(routeMatchNormalized);

  for (const param of routeParams) {
    routeMatchNormalized = routeMatchNormalized.replace(`:${param}`, '([\\w-+])*');
  }

  const regex = new RegExp(routeMatchNormalized);
  return regex.test(route);
}

function matchRouteWith(routeMatch: string, route: string = instance.getCurrentRoute()): Match | boolean {
  const context = getContextName();
  const url = `/${context}/${routeMatch}`.replace(new RegExp('//+', 'g'), '/');

  return matchPath(route, url) as unknown as boolean || false;
}

export {
  instance as default,
  HistoryService,
  init,
  listenHistoryChange,
  listenMatch,
  matchRouteExactWithQueryParam,
  matchRouteWith,
  setMatch,
};
